import React from "react";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {NumericFormat} from "react-number-format";
import {useDealContext} from "../../contexts/DealContext";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";

// Bordered currency input matching the reference's .lender-input-wrap — the
// wrapper owns the border/focus ring, the CustomCurrencyInput renders bare.
const CurrencyField = ({ value, onChange, className = '', disabled = false }) => (
    <div className={`lender-input-wrap ${className} ${disabled ? 'is-disabled' : ''}`.trim()}>
        <span className={'lender-suffix'}>$</span>
        <CustomCurrencyInput
            value={value}
            allowNull={true}
            onChange={onChange}
            disabled={disabled}
            InputProps={{}}
        />
    </div>
)

// How the customer held the trade. Owned Outright has no payoff, so it hides
// Payoff and Net; the other inputs are shared. The closed box shows `label`,
// the menu `menuLabel`.
const TRADE_IN_TYPES = [
    { value: 'FINANCED', label: 'Financed', menuLabel: 'Financed' },
    { value: 'LEASED', label: 'Leased', menuLabel: 'Leased' },
    { value: 'OWNED', label: 'Owned', menuLabel: 'Owned (Outright)' },
]

// Enter opens or picks from the dropdown; keep the Deal page's document-level
// Enter from also re-running Calculate. The type waits for Update.
const stopEnter = (e) => e.key === 'Enter' && e.stopPropagation()

// The Structure panel's select menu, anchored to the header's right edge. The
// menu takes the box's width as a minimum and grows to fit its labels.
const selectMenuProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
    marginThreshold: 4,
    MenuListProps: { className: 'structure-select-menu' },
    PaperProps: { className: 'structure-select-paper' },
}

export const DealPageTradeIn = () => {
    const { dealState, setDealStateWithUpdate, isHistorical } = useDealContext()

    if (!dealState) return null;

    const isOwned = dealState.trade_in_type === 'OWNED'
    // An owned trade's payoff is always 0, whatever the deal holds.
    const payoffOf = (state) => state.trade_in_type === 'OWNED' ? 0 : (Number(state.trade_payoff) || 0)

    // Net = Cash Value − Payoff (read-only).
    const net = (Number(dealState.trade_in_amount) || 0) - payoffOf(dealState)

    // Equity / Profit are only editable once there's an actual ACV (Cash Value).
    const hasAcv = (Number(dealState.trade_in_amount) || 0) > 0

    // Profit is the stored intent; Equity is always Net − Profit. So with Profit
    // at its default (0) the whole Net flows to Equity, and any change to Net
    // (Cash Value or Payoff) re-derives Equity while Profit stays put.
    const recalcFromNet = (updates) => {
        const next = { ...dealState, ...updates }
        const newNet = (Number(next.trade_in_amount) || 0) - payoffOf(next)
        const profit = Math.min(Math.max(Number(next.trade_profit) || 0, 0), Math.max(newNet, 0))
        setDealStateWithUpdate({ ...updates, trade_profit: profit, trade_equity: newNet - profit })
    }

    // Editing Equity directly is the inverse: it sets Profit = Net − Equity.
    const setEquity = (value) => {
        const equity = Math.min(Math.max(Number(value) || 0, 0), Math.max(net, 0))
        setDealStateWithUpdate({ trade_equity: equity, trade_profit: net - equity })
    }
    // Editing Profit re-derives Equity from the current Net.
    const setProfit = (value) => recalcFromNet({ trade_profit: value })

    // Financed <-> Leased changes only the type. Owned Outright clears the payoff
    // so it can't be saved hidden, and Equity re-derives from Net = Cash Value.
    const setType = (type) => type === 'OWNED'
        ? recalcFromNet({ trade_in_type: type, trade_payoff: null })
        : setDealStateWithUpdate({ trade_in_type: type })

    return (
        <Box className={`panel-tradein ${isOwned ? 'is-owned' : ''}`.trim()}>
            <div className={'panel-header'}>
                <div className={'panel-title'}>Trade In</div>
                <Select
                    className={'Select structure-select-mui'}
                    value={dealState.trade_in_type || 'FINANCED'}
                    disabled={isHistorical}
                    inputProps={{ 'aria-label': 'Trade-in type' }}
                    renderValue={(value) => TRADE_IN_TYPES.find((type) => type.value === value)?.label}
                    MenuProps={selectMenuProps}
                    // On the root around the box, which the portalled menu's
                    // events also bubble through; Select's own onKeyDown lands
                    // on its hidden input.
                    slotProps={{ root: { onKeyDown: stopEnter } }}
                    onChange={(e) => setType(e.target.value)}
                >
                    {TRADE_IN_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>{type.menuLabel}</MenuItem>
                    ))}
                </Select>
            </div>
            <div className={'panel-body'}>
                <div className={'lender-row'}>
                    <span className={'lender-key'}>Cash Value:</span>
                    <CurrencyField
                        value={dealState.trade_in_amount}
                        disabled={isHistorical}
                        onChange={(value) => recalcFromNet({trade_in_amount: value})}
                    />
                </div>

                {!isOwned && <>
                    <div className={'lender-row'}>
                        <span className={'lender-key'}>Payoff:</span>
                        <CurrencyField
                            value={dealState.trade_payoff}
                            disabled={isHistorical}
                            onChange={(value) => recalcFromNet({trade_payoff: value})}
                        />
                    </div>

                    {/* Net — read-only, aligned to the input column */}
                    <div className={'lender-row'}>
                        <span className={'lender-key'}>Net:</span>
                        <span className={'tradein-net'}>
                            <span className={'lender-suffix'}>$</span>
                            <span className={'tradein-net-value'}>
                                <NumericFormat
                                    decimalScale={2}
                                    value={net}
                                    displayType={'text'}
                                    thousandSeparator
                                />
                            </span>
                        </span>
                    </div>
                </>}

                {/* Equity + Profit share one row */}
                <div className={'lender-row tradein-equity-row'}>
                    <span className={'lender-key tradein-equity-label'}>Equity:</span>
                    <div className={'tradein-equity-group'}>
                        <CurrencyField
                            value={dealState.trade_equity}
                            onChange={(value) => setEquity(value)}
                            disabled={isHistorical || !hasAcv}
                        />
                        <span className={'tradein-profit-label'}>Profit:</span>
                        <CurrencyField
                            className={'tradein-profit-input'}
                            value={dealState.trade_profit}
                            onChange={(value) => setProfit(value)}
                            disabled={isHistorical || !hasAcv}
                        />
                    </div>
                </div>
            </div>
        </Box>
    )
}
